import {
  Box,
  yesterdayDate,
  nameRegExp,
  TextFieldForm
} from 'web-library';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import { stringToSemanticDisplay } from 'web-library/utils/DateUtils';
import { AdminProfileResponseObject } from 'web-library/models/AccountManagementConfiguration';
import ToggleDisplayEditFormItem from '../../../components/ToggleDisplayEditFormItem';
import { useStyles } from './index';
import dayjs from 'dayjs';
import ControlledCustomSelect from '../../../components/form-items/ControlledCustomSelect';
import { CreateEditAdminSchemaData } from './CreateEditAdminSchema';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

interface AdminPersonalParticularsProps {
  isEditState: boolean;
  formHook?: UseFormReturn<CreateEditAdminSchemaData>;
  adminProfileDetails?: AdminProfileResponseObject;
  checkDisable?: boolean;
}

const AdminPersonalParticulars = ({
  isEditState,
  formHook,
  adminProfileDetails,
  checkDisable,
}: AdminPersonalParticularsProps) => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const [dateOfBirth, setDateOfBirth] = useState<string>(adminProfileDetails?.dateOfBirth || '');

  const getIdTypeValueText = (credentialType?: string) => {
    switch (credentialType) {
      case 'SINGPASS':
        return 'SingPass';
      case 'MIMS':
        return 'MIMS';
      default:
        return 'CP ID';
    }
  };
  return (
    <Box className={classes.responsiveFlexContainer}>
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.Name')}
        valueText={adminProfileDetails?.name}
        containerClassName={classes.formElementContainer}
        EditComponent={
          formHook && (
            <TextFieldForm
              form={formHook}
              id="inputText-candidateProfile-name"
              label={t('admin->>UserAccountManagement.Name')}
              placeholder="Enter name"
              isRequired
              error={!!formHook?.formState.errors.name}
              helperText={formHook?.formState.errors?.name?.message?.toString()}
              name="name"
              regExpToBlock={nameRegExp}
              type="text"
              defaultValue={adminProfileDetails?.name}
              fullWidth
            />
          )
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.IdType')}
        valueText={getIdTypeValueText(adminProfileDetails?.credentialType)}
        containerClassName={classes.halfWidthLeftItem}
        EditComponent={
          formHook && (
            <ControlledCustomSelect
              id="inputSelect-accountType"
              labelId="inputSelectLabel-accountType"
              label={t('admin->>UserAccountManagement.IdType')}
              name="accountType"
              control={formHook.control}
              menuItems={[
                { key: 'USERNAME', value: 'USERNAME', children: 'CP ID' },
                { key: 'SINGPASS', value: 'SINGPASS', children: 'SingPass' },
                { key: 'MIMS', value: 'MIMS', children: 'MIMS' },
              ]}
              defaultValue={adminProfileDetails?.credentialType || 'USERNAME'}
              variant="filled"
              isRequired
            />
          )
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.NricFinPassportNo')}
        valueText={adminProfileDetails?.adminId}
        containerClassName={classes.halfWidthRightItem}
        EditComponent={
          <TextFieldForm
            form={formHook}
            id="inputText-candidateProfile-nricfinpassport"
            label={t('admin->>UserAccountManagement.NricFinPassportNo')}
            isRequired
            error={!!formHook?.formState.errors.nricFinPassport}
            helperText={formHook?.formState.errors?.nricFinPassport?.message?.toString()}
            name="nricFinPassport"
            type="text"
            disabled={checkDisable}
            defaultValue={adminProfileDetails?.adminId}
            fullWidth
          />
        }
      />

      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.DateOfBirth')}
        valueText={stringToSemanticDisplay(adminProfileDetails?.dateOfBirth || '')}
        containerClassName={classes.formElementContainer}
        EditComponent={
            formHook && <ControlledDatePicker
              control={formHook?.control}
            label={t('admin->>UserAccountManagement.DateOfBirth')}
            onChange={(date) => {
              setDateOfBirth(date || '');
              formHook?.setValue('dateOfBirth', date || '');
            }}
            inputVariant="filled"
            error={!!formHook?.formState.errors.dateOfBirth}
            helperText={formHook?.formState.errors?.dateOfBirth?.message?.toString()}
            isRequired
            maxDate={dayjs(yesterdayDate)}
            name="dateOfBirth"
            disableFuture
              defaultValue={dateOfBirth}
          />
        }
      />
    </Box>
  );
};

export default AdminPersonalParticulars;
